'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '@/lib/api';
import { JobResponse, HistoryItem, QueueStats, HealthResponse, JobCreate, BatchJobCreate, Series } from '@/lib/types';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';
import { useScratchpadApplyTarget } from '@/lib/scratchpad';
import GenerationForm from '@/components/GenerationForm';
import JobCard from '@/components/JobCard';
import HistoryPanel from '@/components/HistoryPanel';
import StatusBar from '@/components/StatusBar';
import ImageModal from '@/components/ImageModal';
import SeriesPanel from '@/components/SeriesPanel';

export default function Home() {
  const [jobs, setJobs] = useState<JobResponse[]>([]);
  const jobsRef = useRef<JobResponse[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [selectedJob, setSelectedJob] = useState<JobResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<Partial<JobCreate>>({});
  const [series, setSeries] = useState<Series[]>([]);
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const toast = useToast();
  const { confirm } = useConfirm();

  // Lets the scratchpad drop a copied prompt straight into the form. Merged into the
  // existing seed so the settings that came with a previous "Use Settings" survive.
  useScratchpadApplyTarget(useCallback((text: string) => {
    setFormValues((prev) => ({ ...prev, prompt: text }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []));

  const fetchData = useCallback(async () => {
    try {
      const [healthRes, statsRes, jobsRes, historyRes, seriesRes] = await Promise.all([
        api.getHealth(),
        api.getQueueStats(),
        api.listJobs(undefined, 500, 0, selectedSeriesId ?? undefined),
        api.getHistory(50),
        api.getSeries(),
      ]);
      setHealth(healthRes);
      setStats(statsRes);
      setJobs(jobsRes);
      jobsRef.current = jobsRes;
      setHistory(historyRes);
      setSeries(seriesRes);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [selectedSeriesId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      const hasPendingOrProcessing = jobsRef.current.some(
        j => j.status === 'pending' || j.status === 'processing'
      );
      if (hasPendingOrProcessing) {
        fetchData();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSubmit = async (jobData: JobCreate) => {
    setIsSubmitting(true);
    try {
      await api.createJob(jobData);
      await fetchData();
    } catch (error) {
      console.error('Failed to create job:', error);
      toast.error('Failed to create job: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBatchSubmit = async (request: BatchJobCreate) => {
    // Confirmation for large batches
    const preview = await api.previewPermutations(request.prompt);
    if (preview.total_permutations > 20) {
      const confirmed = await confirm({
        title: 'Large Batch',
        message: `This will create ${preview.total_permutations} jobs. Continue?`,
        confirmText: 'Generate All',
        variant: 'warning',
      });
      if (!confirmed) return;
    }

    setIsSubmitting(true);
    try {
      const result = await api.generatePermutations(request);
      await fetchData();
      toast.success(`Created ${result.total_jobs} jobs from permutations`);
    } catch (error) {
      console.error('Failed to create batch jobs:', error);
      toast.error('Failed to create batch jobs: ' + (error as Error).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (jobId: string, force: boolean = false) => {
    try {
      await api.cancelJob(jobId, force);
      await fetchData();
    } catch (error) {
      console.error('Failed to cancel job:', error);
      toast.error('Failed to cancel job: ' + (error as Error).message);
    }
  };

  const handleRetry = async (jobId: string) => {
    try {
      await api.retryJob(jobId);
      await fetchData();
    } catch (error) {
      console.error('Failed to retry job:', error);
    }
  };

  const handleDeleteJob = async (jobId: string) => {
    try {
      await api.deleteJobPermanent(jobId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete job:', error);
    }
  };

  const handleDeleteHistoryItem = async (itemId: number) => {
    try {
      await api.deleteHistoryItem(itemId);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete history item:', error);
    }
  };

  const handleReuseFromHistory = (item: HistoryItem) => {
    setFormValues({
      prompt: item.prompt,
      width: item.params.width,
      height: item.params.height,
      steps: item.params.steps,
      cfg: item.params.cfg,
      seed: item.params.seed,
      series_id: item.params.series_id ?? undefined,
    });
    // For batch items, also set the selected series filter
    if (item.job_type === 'batch' && item.params.series_id) {
      setSelectedSeriesId(item.params.series_id);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReuseFromJob = (job: JobResponse) => {
    setFormValues({
      prompt: job.prompt,
      width: job.width,
      height: job.height,
      steps: job.steps,
      cfg: job.cfg,
      seed: job.seed,
    });
    setSelectedJob(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearHistory = async (period?: 'today' | 'yesterday' | 'all') => {
    const periodLabels = {
      today: "today's",
      yesterday: "yesterday's",
      all: 'all',
    };
    const label = periodLabels[period || 'all'];

    const confirmed = await confirm({
      title: 'Clear History',
      message: `This will permanently delete ${label} history items.`,
      confirmText: period === 'all' ? 'Clear All' : `Clear ${label.charAt(0).toUpperCase() + label.slice(1)}`,
      variant: 'danger',
    });

    if (confirmed) {
      try {
        const result = await api.clearHistoryByPeriod(period || 'all');
        await fetchData();
        toast.success(`Cleared ${result.cleared} history items`);
      } catch (error) {
        console.error('Failed to clear history:', error);
        toast.error('Failed to clear history');
      }
    }
  };

  const activeJobs = jobs.filter(j => j.status === 'pending' || j.status === 'processing');
  const completedJobs = jobs.filter(j => j.status === 'completed');

  // Calculate total images across all series for "All Images" count
  const totalImages = series.reduce((sum, s) => sum + s.image_count, 0) +
    jobs.filter(j => j.status === 'completed' && !j.series_id).length;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <StatusBar health={health} stats={stats} onRefresh={fetchData} />

      <div className="flex-1 flex overflow-hidden">
        {/* Series Sidebar */}
        <aside className="hidden lg:block w-64 border-r border-[var(--border-subtle)] flex-shrink-0 overflow-y-auto">
          <SeriesPanel
            series={series}
            selectedSeriesId={selectedSeriesId}
            totalImages={totalImages}
            onSelectSeries={setSelectedSeriesId}
            onSeriesChange={fetchData}
          />
        </aside>

        {/* Main Content - scrollable independently */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="max-w-6xl mx-auto px-6 py-8 space-y-10">
            {/* Header */}
            <div className="space-y-2">
              <h1 className="text-3xl font-semibold tracking-tight">Create</h1>
              <p className="text-[var(--text-muted)]">Generate images with AI</p>
            </div>

            {/* Generation Form */}
            <GenerationForm
              key={JSON.stringify(formValues)}
              onSubmit={handleSubmit}
              onBatchSubmit={handleBatchSubmit}
              isLoading={isSubmitting}
              initialValues={formValues}
              series={series}
              selectedSeriesId={selectedSeriesId}
            />

            {/* Active Jobs */}
            {activeJobs.length > 0 && (
              <section className="space-y-4">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  In Progress
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {activeJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onCancel={handleCancel}
                      onSelect={setSelectedJob}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Gallery */}
            <section className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-medium text-[var(--text-muted)] uppercase tracking-wider">
                  Gallery
                </h2>
                {completedJobs.length > 0 && (
                  <span className="text-xs text-[var(--text-muted)]">
                    {completedJobs.length} images
                  </span>
                )}
              </div>

              {completedJobs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-full bg-[var(--bg-elevated)] flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-[var(--text-muted)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                  </div>
                  <p className="text-[var(--text-muted)]">No images yet</p>
                  <p className="text-sm text-[var(--text-muted)] mt-1">Your creations will appear here</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {completedJobs.map((job) => (
                    <JobCard
                      key={job.job_id}
                      job={job}
                      onSelect={setSelectedJob}
                      onDelete={handleDeleteJob}
                      onCopy={handleReuseFromJob}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>

        {/* History Sidebar - scrollable independently */}
        <aside className="hidden lg:block w-72 border-l border-[var(--border-subtle)] flex-shrink-0 overflow-hidden">
          <HistoryPanel
            history={history}
            onReuseParams={handleReuseFromHistory}
            onDeleteItem={handleDeleteHistoryItem}
            onClearHistory={handleClearHistory}
          />
        </aside>
      </div>

      {/* Image Modal - Fullscreen with navigation */}
      {selectedJob && selectedJob.status === 'completed' && (
        <ImageModal
          job={selectedJob}
          jobs={completedJobs}
          onClose={() => setSelectedJob(null)}
          onReuseParams={handleReuseFromJob}
          onDelete={handleDeleteJob}
          onNavigate={setSelectedJob}
          series={series}
          onSeriesChange={fetchData}
        />
      )}
    </div>
  );
}
